/* eslint-disable no-param-reassign */
/* eslint-disable react/require-default-props */
import {
  InputAdornment,
  OutlinedInput,
  OutlinedInputProps,
  Popover,
  PopoverProps as TPopoverProps,
} from '@material-ui/core';
import { DateRangeOutlined } from '@material-ui/icons';
import PaperOutlined from '@src/components/UI/PaperOutlined';
import { safeFnsFormat } from '@src/utils/fnsDateUtils';
import usePopoverState from '@src/utils/usePopoverState';
import { endOfDay, isValid } from 'date-fns';
import React, { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import Calendar, { CalendarProps } from './Calendar';

export type DatePickerInputProps<WithRange extends boolean = boolean> = {
  className?: string;
  dateFormat?: string;
  leftPanel?: React.ReactNode;
  PopoverProps?: Partial<TPopoverProps>;
  InputProps?: Partial<OutlinedInputProps>;
  autoCloseRange?: boolean;
  label?: React.ReactNode;
  /** When false, closes this popover (e.g. when parent popover closes). Prevents freeze when used inside another Popover. */
  parentPopoverOpen?: boolean;
} & CalendarProps<WithRange>;

export const defaultDateFormat = 'E MMM d, Y';
export const defaultTimeFormat = 'HH:mm';

export type DatePickerInputHandle = {
  focus: () => void;
  open: () => void;
  isOpen: () => boolean;
};

const DatePickerInputInner = <WithRange extends boolean = boolean>(
  {
    className,
    selectsRange,
    dateFormat = defaultDateFormat,
    timeFormat = defaultTimeFormat,
    value,
    onChange,
    PopoverProps = {},
    InputProps = {},
    leftPanel,
    showTimeSelect,
    showTimeSelectOnly,
    autoCloseRange,
    label,
    parentPopoverOpen = true,
    ...props
  }: DatePickerInputProps<WithRange>,
  ref: React.Ref<DatePickerInputHandle>
) => {
  const popoverState = usePopoverState();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => inputRef.current?.focus(),
      open: () => {
        if (rootRef.current) popoverState.open(rootRef.current);
      },
      isOpen: () => Boolean(popoverState.anchorEl),
    }),
    [popoverState]
  );

  // Reset popover when parent (e.g. filters panel) closes so it doesn't freeze on reopen
  useEffect(() => {
    if (parentPopoverOpen === false) {
      popoverState.close();
    }
  }, [parentPopoverOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const textFieldValue = useMemo(() => {
    const format = [
      dateFormat,
      showTimeSelect || showTimeSelectOnly ? timeFormat : undefined,
    ]
      .filter(Boolean)
      .join(' ');

    if (selectsRange) {
      return value
        .map(date => safeFnsFormat(date, format))
        .filter(Boolean)
        .join(' - ');
    }
    return safeFnsFormat(value, format);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectsRange, dateFormat, timeFormat]);

  const closeHandler = () => {
    if (selectsRange && autoCloseRange) {
      const [start, end] = value || [];
      if (isValid(start) && !isValid(end)) {
        onChange([start, endOfDay(start as Date)]);
      }
    }
    popoverState.close();
  };

  return (
    <>
      <OutlinedInput
        ref={rootRef}
        inputRef={inputRef}
        label={label}
        className={`${className} ${popoverState.isOpen && 'Mui-focused'}`}
        value={textFieldValue}
        fullWidth={false}
        onClick={popoverState.open}
        spellCheck={false}
        autoComplete="off"
        endAdornment={
          <InputAdornment position="end">
            <DateRangeOutlined fontSize="small" />
          </InputAdornment>
        }
        {...InputProps}
      />
      <Popover
        open={popoverState.isOpen}
        anchorEl={popoverState.anchorEl}
        onClose={closeHandler}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          component: PaperOutlined,
          style: { marginBlock: 8 },
        }}
        {...PopoverProps}
      >
        <Calendar
          {...({
            selectsRange,
            value,
            onChange,
            leftPanel,
            timeFormat,
            showTimeSelect,
            showTimeSelectOnly,
            ...props,
          } as CalendarProps)}
        />
      </Popover>
    </>
  );
};

const DatePickerInput = React.forwardRef(DatePickerInputInner) as <
  WithRange extends boolean = boolean
>(
  props: DatePickerInputProps<WithRange> & {
    ref?: React.Ref<DatePickerInputHandle>;
  }
) => React.ReactElement;

export default DatePickerInput;
